import { useStableKeys } from '../../hooks/useStableKeys';
import { ArrowDownIcon, ArrowUpIcon, PlusIcon, XIcon } from 'lucide-react';
import { undoToast } from './undoToast';
import { Field } from './Field';
import { ImageField } from './ImageField';
import { MediaField } from './MediaField';
import { isMediaShape } from '../../core/rules/media';
import { NumberInput } from './NumberInput';
import { useAdmin } from '../AdminContext';
import { fieldLabels, humanize, imageKeys, selectOptions, type FieldMeta } from '../adminLabels';
import { btnIcon, inputCls } from './styles';
import { move } from '../editing';
import { fill } from '../../utils/copy';
import { wordTips } from '../../data/wordList';
export type Json = string | number | boolean | null | Json[] | {
  [key: string]: Json;
};

/** Hoe een veld op díe plek heet (data/pageSections.ts), als het algemene label niet genoeg zegt. */
export type FieldOverrides = Record<string, {
  label: string;
  hint?: string;
}>;
type Props = {
  value: Json;
  onChange: (value: Json) => void;
  fieldKey?: string;
  depth?: number;
  /** Labels per veld voor dit blok; gelden alleen voor de velden direct in het blok. */
  labels?: FieldOverrides;
  /** Label en uitleg van dit veld zelf (doorgegeven vanuit het blok). */
  override?: {
    label: string;
    hint?: string;
  };
};

/**
 * Richtlengtes (docs/VORMGEVING.md › Woorden): een knop past op één regel, een kop op twee.
 * Het beheer telt mee en kleurt bij te lang; opslaan blijft altijd mogelijk.
 */
const MAX_LENGTH: Record<string, number> = {
  cta: 28,
  secondaryCta: 28,
  quizCta: 32,
  addCta: 24,
  salonCta: 32,
  askCta: 24,
  shopCta: 28,
  eyebrow: 50,
  text: 40,
  accent: 30,
  title: 48
};

/** Langer dan dit wordt een tekstvak in plaats van één regel. */
const LONG_TEXT = 80;
/** Ongeveer zoveel tekens per regel in een tekstvak; zo groeit het vak mee (3–8 regels). */
const CHARS_PER_ROW = 90;
const rowsFor = (text: string) => Math.min(8, Math.max(3, Math.ceil(text.length / CHARS_PER_ROW)));

/** Velden die de site weglaat als ze leeg zijn, met wat Sarah dan ziet. */
const EMPTY_HIDES: Record<string, string> = {
  cta: 'Leeg: deze knop staat niet op de site.',
  secondaryCta: 'Leeg: deze knop staat niet op de site.',
  eyebrow: 'Leeg: er staat geen regel boven de kop.',
  body: 'Leeg: er staat geen tekst onder de kop.'
};
/** Gedachtestreepjes: de huisstijl gebruikt een punt, komma of dubbele punt. */
const DASH = /\s[—–]\s|—/;
const isObject = (v: Json): v is {
  [key: string]: Json;
} => typeof v === 'object' && v !== null && !Array.isArray(v);

/** Een leeg exemplaar met dezelfde vorm — voor "toevoegen" in een lijst. */
const emptied = (v: Json): Json => {
  if (typeof v === 'string') return '';
  if (typeof v === 'number') return 0;
  if (typeof v === 'boolean') return false;
  if (Array.isArray(v)) return [];
  if (isObject(v)) return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, emptied(x)]));
  return null;
};

/** Eerste stukje tekst uit een item, als titel van de kaart. */
const titleOf = (v: Json): string => {
  if (typeof v === 'string') return v;
  if (isObject(v)) {
    for (const key of ['title', 'name', 'text', 'code', 'quote', 'label']) {
      const x = v[key];
      if (typeof x === 'string' && x) return x;
    }
  }
  return '';
};

/**
 * Bouwt automatisch een formulier op uit de vorm van de data.
 * Zo is elke tekst op de site te bewerken zonder dat er per blok een scherm
 * gebouwd hoeft te worden — en een nieuw tekstveld in data/pages.ts
 * verschijnt hier vanzelf.
 */
/**
 * Geneste groepen zonder kaders: een zachte vlakkleur die per laag wisselt (wit paneel → zand →
 * wit → …), zodat je ziet wat bij elkaar hoort zonder doos-in-doos.
 */
const tint = (depth: number) => depth % 2 === 1 ? 'bg-shell' : 'bg-white';
export function ContentEditor({
  value,
  onChange,
  fieldKey,
  depth = 0,
  labels,
  override
}: Props) {
  const base = fieldKey ? fieldLabels[fieldKey] : undefined;
  if (base?.hidden) return null;
  const meta = override ? {
    ...base,
    label: override.label,
    hint: override.hint ?? base?.hint
  } : base;
  const label = override?.label ?? (fieldKey ? humanize(fieldKey) : '');
  if (typeof value === 'string') {
    return <TextField fieldKey={fieldKey ?? ''} label={label} hint={meta?.hint} value={value} onChange={onChange} data-id="element-4920" data-mp="3by5jjkakhzky" />;
  }
  if (typeof value === 'number') {
    return <Field label={label} hint={meta?.hint} data-id="element-4921" data-mp="32yiiizlr2vsf">
        <NumberInput decimals value={Number.isFinite(value) ? value : 0} onChange={v => v !== undefined && onChange(v)} className="max-w-[12rem]" data-id="element-4922" data-mp="331wiy2ausux8" />
      </Field>;
  }
  if (typeof value === 'boolean') {
    return <label className="flex min-h-11 items-center gap-3 text-[15px] text-ink sm:min-h-0 sm:gap-2 sm:text-[14px]" data-id="element-4923" data-mp="2oae62ddajgi3">
        <input type="checkbox" checked={value} onChange={e => onChange(e.target.checked)} className="h-5 w-5 shrink-0 accent-clay sm:h-4 sm:w-4" data-id="element-4924" data-mp="2k9vemkfb8cgf" />
        {label}
      </label>;
  }
  if (Array.isArray(value)) {
    return <ListField fieldKey={fieldKey ?? ''} label={label} value={value} onChange={onChange} depth={depth} data-id="element-4925" data-mp="2ac7k9vlm96ys" />;
  }

  // Een beeld ({ image, alt, focus?, video? }): één veld met uitsnede en video, niet vier losse.
  if (isMediaShape(value)) {
    return <MediaField label={label || 'Beeld'} hint={meta?.hint} value={value} onChange={next => onChange(next as unknown as Json)} data-id="element-4926" data-mp="zr1mi0m578cu" />;
  }
  if (isObject(value)) {
    const entries = Object.entries(value).filter(([k]) => !fieldLabels[k]?.hidden);
    const body = <div className="space-y-5" data-id="element-4927" data-mp="73xbnf0dbzt5">
        {entries.map(([k, v]) => <ContentEditor key={k} fieldKey={k} value={v} depth={depth + 1} override={labels?.[k]} onChange={next => onChange({
        ...value,
        [k]: next
      })} data-id="element-4928" data-mp="c2igjrqkf7nx" />)}
      </div>;
    if (!fieldKey || depth === 0) return body;
    return <fieldset className={`rounded-md p-4 ${tint(depth)}`} data-id="element-4929" data-mp="kvvfpbf835ig">
        <legend className="px-1.5 text-[13px] font-medium text-ink" data-id="element-4930" data-mp="3kg7kz1cugdtm">{label}</legend>
        {meta?.hint && <p className="-mt-1 mb-3 text-[12px] text-muted" data-id="element-4931" data-mp="3ohst69efo3xh">{meta.hint}</p>}
        {body}
      </fieldset>;
  }
  return null;
}
function TextField({
  fieldKey,
  label,
  hint,
  value,
  onChange
}: {
  fieldKey: string;
  label: string;
  hint?: string;
  value: string;
  onChange: (v: Json) => void;
}) {
  const {
    catalog
  } = useAdmin();
  const base: FieldMeta | undefined = fieldLabels[fieldKey];
  const meta: FieldMeta | undefined = base ? {
    ...base,
    hint
  } : hint ? {
    label,
    hint
  } : undefined;
  const options = selectOptions[fieldKey];
  const max = MAX_LENGTH[fieldKey];
  const tips = wordTips(value);
  if (imageKeys.has(fieldKey)) {
    return <Field label={label} hint={meta?.hint} as="div" data-id="element-4932" data-mp="7sj1gd525cls">
        <div className="max-w-[14rem]" data-id="element-4933" data-mp="lr8zpw3m5ibq">
          <ImageField value={value} onChange={onChange} fit="cover" aspect="aspect-[4/3]" data-id="element-4934" data-mp="lo3c6lamuoi4" />
        </div>
      </Field>;
  }
  if (options) {
    return <Field label={label} hint={meta?.hint} data-id="element-4935" data-mp="1x4bk728u2s16">
        <select value={value} onChange={e => onChange(e.target.value)} className={`${inputCls} max-w-xs`} data-id="element-4936" data-mp="1x77iirolfiq5">
          {options.map(o => <option key={o.value} value={o.value} data-id="element-4937" data-mp="20m8ywbjlzfb6">
              {o.label}
            </option>)}
        </select>
      </Field>;
  }
  if (fieldKey === 'pickId') {
    return <Field label={label} hint={meta?.hint} data-id="element-4938" data-mp="1qpw75j7mjbj2">
        <select value={value} onChange={e => onChange(e.target.value)} className={inputCls} data-id="element-4939" data-mp="1qr7ajhx5qh9n">
          {!catalog.products.some(p => p.id === value) && <option value={value} data-id="element-4940" data-mp="2jd8kr3vt4908">Onbekend: {value}</option>}
          {catalog.products.map(p => <option key={p.id} value={p.id} data-id="element-4941" data-mp="2od4msxky1qd5">
              {p.brand} · {p.name}
            </option>)}
        </select>
      </Field>;
  }
  const long = meta?.long || value.length > LONG_TEXT;
  const filled = value.includes('{') ? fill(value) : null;
  return <Field label={label} hint={meta?.hint} data-id="element-4942" data-mp="32cmhftkx7xuf">
      {long ? <textarea value={value} onChange={e => onChange(e.target.value)} rows={rowsFor(value)} className={`${inputCls} resize-y leading-relaxed`} data-id="element-4943" data-mp="32bbchqfqok1q" /> : <input value={value} onChange={e => onChange(e.target.value)} className={inputCls} data-id="element-4944" data-mp="171d02z02nr86" />}
      {filled && filled !== value && <span className="mt-1.5 block text-[12px] leading-snug text-muted" data-id="element-4945" data-mp="1293cvqqcd59l">
          <span className="text-sage" data-id="element-4946" data-mp="y9kw975jzgck">Op de site:</span> {filled}
        </span>}
      {!value.trim() && EMPTY_HIDES[fieldKey] && <span className="mt-1.5 block text-[12px] text-muted" data-id="element-4947" data-mp="t7lsvx9tcjfm">{EMPTY_HIDES[fieldKey]}</span>}
      {max && value.trim() && <span className={`mt-1.5 block text-[12px] tabular-nums ${value.length > max ? 'text-clay' : 'text-muted'}`} data-id="element-4948" data-mp="1lynb678syv3d">
          {value.length}/{max} tekens{value.length > max ? ': past mogelijk niet op één regel' : ''}
        </span>}
      {DASH.test(value) && <span className="mt-1.5 block text-[12px] text-muted" data-id="element-4949" data-mp="1c2ah389cif83">Tip: een punt, komma of dubbele punt leest rustiger dan een gedachtestreepje.</span>}
      {tips.map(t => <span key={t} className="mt-1.5 block text-[12px] text-muted" data-id="element-4950" data-mp="jg7d8axqqeq">
          <span className="text-clay" data-id="element-4951" data-mp="3mn9sfayzyoug">Vaste woorden:</span> {t}
        </span>)}
    </Field>;
}
function ListField({
  fieldKey,
  label,
  value,
  onChange,
  depth
}: {
  fieldKey: string;
  label: string;
  value: Json[];
  onChange: (v: Json) => void;
  depth: number;
}) {
  const meta = fieldLabels[fieldKey];
  const simple = value.every(v => typeof v === 'string');
  const template: Json = value.length ? emptied(value[0]) : '';
  const set = (i: number, next: Json) => onChange(value.map((v, j) => j === i ? next : v));

  // Vaste sleutels per rij (los van de positie), zodat focus en invoer bij de juiste rij
  // blijven na verplaatsen of verwijderen. Houdt de lengte gelijk als de lijst van buitenaf wijzigt.
  const rows = useStableKeys(value.length);
  const reorder = (from: number, to: number) => {
    rows.move(from, to);
    onChange(move(value, from, to));
  };
  const remove = (i: number) => {
    const before = value;
    const beforeIds = rows.snapshot();
    const name = titleOf(value[i]);
    rows.remove(i);
    onChange(value.filter((_, j) => j !== i));
    // Eén klik op het kruisje wist soms een heel citaat of een aflevering: altijd terug te zetten.
    undoToast(name ? `“${name.slice(0, 40)}${name.length > 40 ? '…' : ''}” verwijderd` : 'Verwijderd', () => {
      rows.restore(beforeIds);
      onChange(before);
    });
  };
  const controls = (i: number) => {
    // Schermlezers horen welke regel het is: "Veld 2 van 5 omhoog".
    const name = `${label || 'Regel'} ${i + 1} van ${value.length}`;
    return <span className="flex shrink-0 items-center justify-end" data-id="element-4952" data-mp="194h58wwtzge3">
        <button type="button" onClick={() => reorder(i, i - 1)} disabled={i === 0} aria-label={`${name} omhoog`} className={btnIcon} data-id="element-4953" data-mp="1950m0aaqvyqj">
          <ArrowUpIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-4954" data-mp="1i55j9rm85gqz" />
        </button>
        <button type="button" onClick={() => reorder(i, i + 1)} disabled={i === value.length - 1} aria-label={`${name} omlaag`} className={btnIcon} data-id="element-4955" data-mp="1n3qj3pxkdyew">
          <ArrowDownIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-4956" data-mp="1x9i2ci0kul9a" />
        </button>
        <button type="button" onClick={() => remove(i)} aria-label={`${name} verwijderen`} className={btnIcon} data-id="element-4957" data-mp="1s7ixf3p6vga8">
          <XIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4958" data-mp="1nbtxgz99bxde" />
        </button>
      </span>;
  };
  return <Field label={label} hint={meta?.hint} as="div" data-id="element-4959" data-mp="2fy5872qkvenx">
      {simple ? <ul className="space-y-2" data-id="element-4960" data-mp="2g0j39bl9cqvh">
          {value.map((v, i) => <li key={rows.keys[i]} className="flex flex-col gap-1 sm:flex-row sm:items-start" data-id="element-4961" data-mp="2azvb2mlam3zo" data-mpi={i}>
              {meta?.long || String(v).length > LONG_TEXT ? <textarea value={String(v)} onChange={e => set(i, e.target.value)} rows={rowsFor(String(v))} aria-label={`${label} ${i + 1}`} className={`${inputCls} resize-y leading-relaxed`} data-id="element-4962" data-mp="227jm755tuolt" /> : <input value={String(v)} onChange={e => set(i, e.target.value)} aria-label={`${label} ${i + 1}`} className={inputCls} data-id="element-4963" data-mp="s1p06jzf1nmd" />}
              {controls(i)}
            </li>)}
        </ul> : <ul className="space-y-3" data-id="element-4964" data-mp="iakmdnlh88t4">
          {value.map((v, i) => <li key={rows.keys[i]} className={`rounded-md ${tint(depth + 1)}`} data-id="element-4965" data-mp="n8mesz4slkh9" data-mpi={i}>
              <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-2" data-id="element-4966" data-mp="99wg5f2brcqs">
                <span className="min-w-0 truncate text-[13px] text-muted" data-id="element-4967" data-mp="e9irfiyleiwy">
                  <span className="text-ink" data-id="element-4968" data-mp="3vhmbiw9bd1ma">{i + 1}.</span> {titleOf(v) || 'Nieuw'}
                </span>
                {controls(i)}
              </div>
              <div className="p-4" data-id="element-4969" data-mp="4bbgbgqziv2v">
                <ContentEditor value={v} onChange={next => set(i, next)} depth={depth + 1} data-id="element-4970" data-mp="1tpzp780d9uw5" />
              </div>
            </li>)}
        </ul>}
      <button type="button" onClick={() => onChange([...value, template])} className="mt-2 inline-flex items-center gap-1.5 rounded-md px-2 py-2.5 text-[14px] text-clay transition-colors duration-150 ease-soft hover:bg-sand sm:py-1.5 sm:text-[13px]" data-id="element-4971" data-mp="1yob25rlyklil">
        <PlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4972" data-mp="3sxpxkc5qvibf" />
        Toevoegen
        {label && <span className="sr-only" data-id="element-4973" data-mp="3o4n381t584u6">: {label}</span>}
      </button>
    </Field>;
}